import { ChevronLeft, ChevronRight, Download, Play, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '../../lib/cn.js';

export interface LightboxItem {
  readonly id: string;
  /** Imagem: o arquivo. Vídeo: o player embutido do Bunny. */
  readonly url: string;
  readonly title: string;
  /** Linhas de metadado: data da foto, coordenada, autor. */
  readonly meta?: readonly string[] | undefined;
  readonly kind?: 'imagem' | 'video' | undefined;
  /** Capa do vídeo, usada na tira de miniaturas. */
  readonly thumbnailUrl?: string | undefined;
}

/**
 * Visualizador de imagem e vídeo.
 *
 * Abrir a foto numa aba nova entrega o arquivo cru, sem data, sem coordenada e
 * sem como passar para a próxima — e no celular, custa perder a tela. Aqui a
 * foto é vista no contexto, com os metadados que a tornam prova de fiscalização.
 *
 * Vídeo entra na mesma sequência, mas quem toca é o **player do Bunny**, num
 * `<iframe>`. Não é preguiça de fazer um player próprio: o vídeo é servido em
 * HLS com várias resoluções, e o `<video>` do navegador não lê HLS sozinho fora
 * do Safari. O player deles também resolve legenda, velocidade e tela cheia.
 *
 * Usa `<dialog>` nativo pelo mesmo motivo do modal: prisão de foco, `Esc` e
 * retorno do foco ao gatilho saem prontos do navegador.
 */
export function Lightbox({
  items,
  index,
  onClose,
  onNavigate,
}: {
  items: readonly LightboxItem[];
  /** `null` mantém o visualizador fechado. */
  index: number | null;
  onClose: () => void;
  onNavigate: (index: number) => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  /*
   * Qual foto terminou de carregar, e não um "carregou" solto.
   *
   * Guardando o booleano era preciso zerá-lo num efeito toda vez que o índice
   * mudava — e entre a troca de foto e o efeito o React desenhava um quadro com
   * a foto nova já opaca. Guardando o índice, a resposta sai da própria
   * renderização e não há intervalo nenhum.
   */
  const [loadedIndex, setLoadedIndex] = useState<number | null>(null);
  const loaded = loadedIndex === index;

  const open = index !== null;
  const current = index === null ? undefined : items[index];

  const go = useCallback(
    (step: number) => {
      if (index === null || items.length === 0) return;
      // Percorre em ciclo: chegar ao fim da vistoria volta ao começo.
      onNavigate((index + step + items.length) % items.length);
    },
    [index, items.length, onNavigate],
  );

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleCancel = (event: Event) => {
      event.preventDefault();
      onClose();
    };

    const handleKey = (event: KeyboardEvent) => {
      /*
       * Com o player em foco, as setas são dele — pular para o próximo item no
       * meio de um vídeo, porque a pessoa quis avançar dez segundos, seria
       * exatamente o contrário do esperado.
       */
      if (document.activeElement?.tagName === 'IFRAME') return;
      if (event.key === 'ArrowRight') go(1);
      if (event.key === 'ArrowLeft') go(-1);
    };

    dialog.addEventListener('cancel', handleCancel);
    dialog.addEventListener('keydown', handleKey);
    return () => {
      dialog.removeEventListener('cancel', handleCancel);
      dialog.removeEventListener('keydown', handleKey);
    };
  }, [onClose, go]);

  return (
    <dialog
      ref={dialogRef}
      aria-label="Visualizador de imagem"
      className={cn(
        'm-0 h-dvh max-h-none w-screen max-w-none bg-transparent p-0 text-white',
        'backdrop:bg-[rgba(9,9,11,0.93)]',
      )}
      onClick={(event) => {
        // Clicar no fundo fecha; clicar na imagem ou nos controles, não.
        if (event.target === dialogRef.current) onClose();
      }}
    >
      {current ? (
        <div className="flex h-full flex-col" onClick={(event) => event.stopPropagation()}>
          <header className="flex shrink-0 items-start gap-4 p-4">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{current.title}</p>
              {current.meta && current.meta.length > 0 ? (
                <p className="mt-0.5 truncate font-mono text-[11px] text-white/55">
                  {current.meta.join(' · ')}
                </p>
              ) : null}
            </div>

            {items.length > 1 ? (
              <span className="tabular shrink-0 rounded-sm bg-white/10 px-2 py-1 text-[12px] font-medium">
                {(index ?? 0) + 1} / {items.length}
              </span>
            ) : null}

            {current.kind === 'video' ? null : (
              <a
                href={current.url}
                download
                target="_blank"
                rel="noreferrer noopener"
                aria-label="Baixar imagem"
                title="Baixar imagem"
                className="grid size-9 shrink-0 place-items-center rounded-sm text-white/70 transition-colors hover:bg-white/10 hover:text-white"
              >
                <Download size={17} strokeWidth={1.5} aria-hidden />
              </a>
            )}

            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar"
              className="grid size-9 shrink-0 place-items-center rounded-sm text-white/70 transition-colors hover:bg-white/10 hover:text-white"
            >
              <X size={18} strokeWidth={1.5} aria-hidden />
            </button>
          </header>

          <div
            className="relative flex min-h-0 flex-1 items-center justify-center px-2 pb-4"
            onClick={(event) => {
              // A área vazia ao redor da imagem também fecha.
              if (event.target === event.currentTarget) onClose();
            }}
          >
            {current.kind === 'video' ? (
              <iframe
                key={current.id}
                // A URL já traz token e prazo quando a biblioteca exige assinatura.
                src={`${current.url}${current.url.includes('?') ? '&' : '?'}autoplay=false&preload=true`}
                title={current.title}
                onLoad={() => setLoadedIndex(index)}
                allow="accelerometer; gyroscope; encrypted-media; picture-in-picture; fullscreen"
                allowFullScreen
                className={cn(
                  // 16:9 até onde couber; a moldura preta do player faz o resto.
                  'aspect-video max-h-full w-full max-w-[1200px] border-0 transition-opacity duration-200',
                  loaded ? 'opacity-100' : 'opacity-0',
                )}
              />
            ) : (
              <img
                key={current.id}
                src={current.url}
                alt={current.title}
                onLoad={() => setLoadedIndex(index)}
                className={cn(
                  'max-h-full max-w-full object-contain transition-opacity duration-200',
                  loaded ? 'opacity-100' : 'opacity-0',
                )}
              />
            )}

            {items.length > 1 ? (
              <>
                <button
                  type="button"
                  onClick={() => go(-1)}
                  aria-label="Imagem anterior"
                  className="absolute top-1/2 left-2 grid size-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                >
                  <ChevronLeft size={22} strokeWidth={1.5} aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => go(1)}
                  aria-label="Próxima imagem"
                  className="absolute top-1/2 right-2 grid size-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                >
                  <ChevronRight size={22} strokeWidth={1.5} aria-hidden />
                </button>
              </>
            ) : null}
          </div>

          {items.length > 1 ? (
            <nav
              aria-label="Miniaturas"
              className="flex shrink-0 gap-1.5 overflow-x-auto px-4 pb-4"
            >
              {items.map((item, position) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onNavigate(position)}
                  aria-label={`Ver imagem ${position + 1}`}
                  aria-current={position === index}
                  className={cn(
                    'size-14 shrink-0 overflow-hidden rounded-sm transition-opacity',
                    position === index ? 'opacity-100' : 'opacity-40 hover:opacity-75',
                  )}
                >
                  <span className="relative block size-full bg-white/10">
                    {item.kind === 'video' && !item.thumbnailUrl ? null : (
                      <img
                        src={item.thumbnailUrl ?? item.url}
                        alt=""
                        loading="lazy"
                        className="size-full object-cover"
                      />
                    )}
                    {item.kind === 'video' ? (
                      <span className="absolute inset-0 grid place-items-center bg-black/25">
                        <Play size={15} strokeWidth={2} fill="currentColor" aria-hidden />
                      </span>
                    ) : null}
                  </span>
                </button>
              ))}
            </nav>
          ) : null}
        </div>
      ) : null}
    </dialog>
  );
}

/** Estado do visualizador, para não repetir `useState` em cada tela. */
export function useLightbox() {
  const [index, setIndex] = useState<number | null>(null);
  return {
    index,
    open: (position: number) => setIndex(position),
    close: () => setIndex(null),
    navigate: (position: number) => setIndex(position),
  };
}
